<template>
    <b-modal
        v-model="modalVisibility"
        :title="isNew ? $t('email.new_footer_title') : $t('email.edit_footer_title')"
    >
        <b-alert show variant="info" v-if="isBusy"><fa-icon icon="spinner" class="mr-2" spin />{{ busyMessage }}</b-alert>

        <ValidationObserver ref="footerForm" tag="form" @submit.prevent="handleSubmit">
            <FormSlot :label="$t('email.footer_title')" :rules="{required: true}" v-slot="{errors}">
                <input v-model="form.name" type="text" class="form-control" :placeholder="$t('email.footer_title')" :disabled="isBusy"/>
            </FormSlot>

            <div class="form-group text-left" v-if="templates.length > 0">
                <label>{{$t('email.apply_footer_template')}}</label>
                <b-form-select :options="templates" v-model="activeTemplate" :disabled="isBusy"></b-form-select>
            </div>

            <div class="form-group text-left">
                <label>{{$t('email.default_footer')}}</label>
                <b-form-checkbox v-model="isDefault" :disabled="isBusy"/>
            </div>

            <FormSlot :label="$t('email.footer_content')" :rules="{required: true}" v-slot="{errors}">
                <vue-editor v-model="form.content" :disabled="isBusy" :class="{'opacity-25': isBusy}"/>
            </FormSlot>
        </ValidationObserver>

        <template #modal-footer>
            <div class="text-right">
                <button class="btn btn-outline-secondary mr-2" @click="modalVisibility = false" :disabled="isBusy">{{ $t('_cancel') }}</button>
                <button class="btn btn-primary" @click.prevent="handleSubmit" :disabled="isBusy">
                    <fa-icon v-if="isBusy" icon="spinner" spin />
                    <span v-else>{{ isNew ? $t('_create') : $t('_save')}}</span>
                </button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import {FormSlot} from '@dealer-group/dealerui';
import { VueEditor } from "vue2-editor";
import SystemNotificator from "../../../../../services/SystemNotificator";
import {addFooter, updateFooter, setDefault, fetchFooterTemplates} from "./footersRepository";

export default {
    name: "FooterModal",
    components: {FormSlot, VueEditor},
    props: {
        isVisible: Boolean,
        footerData: Object,
    },
    mounted() {
        fetchFooterTemplates().then(({data}) => {
            this.availableTemplates = data;
        });
    },
    computed: {
        modalVisibility: {
            get() {
                return this.isVisible;
            },
            set() {
                this.$emit('closeModal');
            }
        },
        isNew() {
            return this.footerData.uuid === null;
        },
        templates() {
            if (this.availableTemplates.length > 0) {
                return [
                    { value: null, text: this.$t('email.select_option'), html: ''},
                    ...this.availableTemplates.map(template => ({value: template.uuid, text: template.title}))
                ]
            } else {
                return [];
            }
        },
    },
    watch: {
        footerData: {
            immediate: true,
            deep: true,
            handler() {
                this.resetForm();
            }
        },
        activeTemplate() {
            if (this.activeTemplate !== null) {
                this.form.content = (this.availableTemplates.find(template => template.uuid === this.activeTemplate) || {content: ''}).content;
            }
        },
        modalVisibility() {
            if (this.modalVisibility === true) {
                this.resetForm();
            }
        }
    },
    methods: {
        resetForm() {
            this.form = {
                content: this.footerData.content,
                name: this.footerData.name,
            };
            this.isDefault = this.footerData.default;
            this.activeTemplate = null;
        },

        async handleSubmit() {
            const isValid = await this.$refs.footerForm.validate();
            if (!isValid) {
                return;
            }
            this.isBusy = true;
            this.busyMessage = this.$t('email.footer_saving_busy_message');

            const promise = this.isNew
                ? addFooter(this.form)
                : updateFooter(this.footerData.uuid, this.form)

            promise
                .then(async ({data}) => {
                    this.form.uuid = data.uuid;
                    // update default state if needed
                    if (this.isDefault) {
                        await setDefault(this.form.uuid);
                    }
                    SystemNotificator.success({title: this.$t('email.footer_saved_flash_message')});
                    this.modalVisibility = false;
                    this.$emit('updateList', {data: {
                            ...this.form,
                            default: this.isDefault
                        }, isNew: this.isNew})
                })
                .finally(() => this.isBusy = false)
        }
    },

    data: () => ({
        form: {},
        isBusy: false,
        busyMessage: '',
        activeTemplate: null,
        isDefault: false,
        availableTemplates: []
    })
}
</script>

<style scoped>

</style>